import axios, { AxiosInstance, AxiosError } from 'axios';
import { defaultDomain } from './default-domain';
import { ILoginAccessToken } from './model/login-access-token';
import {
  IStaffMember,
  IActiveOrderSummary,
  ICancelPurchaseOrderRequest,
  ICashDrawer,
  ICashSession,
  ICashSessionReconcileReport,
  IShopDay,
  IDayReport,
  ICurrentDay,
  ICategory,
  ICreateRefundRequest,
  ICancelSaleRequest,
  ICancelSaleResult,
  IMyRegisterResponse,
  IShift,
  ICurrentShift,
  IHandOverResult,
  IStartShiftRequest,
  IEndShiftRequest,
  IHandOverShiftRequest,
  IRecordManualMovementRequest,
  IRefund,
  IZReport,
  ICompleteStockTakeResponse,
  IConfirmDeliveryResponse,
  ICreateDeliveryRequest,
  ICreateProductRequest,
  ICreatePurchaseOrderRequest,
  ICreateSaleRequest,
  ICreateShopRequest,
  ICreateStockItemRequest,
  ICreateSupplierRequest,
  IDelivery,
  ILoginDetails,
  ILoginWithExternalProvider,
  ILoginWithGoogleCode,
  ILowStockReport,
  IProduct,
  IPurchaseOrder,
  IRecordStockTakeCountRequest,
  IRegisterDetails,
  ISale,
  ISalesReport,
  IShopAccountRegister,
  IShopLoyaltyMembership,
  IResolvedMember,
  IMemberTillContext,
  ICreditNote,
  ICreditNotesByMember,
  ICustomerPurchase,
  IStartStockTakeRequest,
  IDraftFromLowStockResult,
  IStockItem,
  IStockItemSupplierLink,
  IStockLocation,
  IStockMovement,
  IStockTake,
  ISupplier,
  IUpdateDeliveryRequest,
  IUpdateProductRequest,
  IUpdatePurchaseOrderRequest,
  IUpdateShopSettingsRequest,
  IUpdateStockItemRequest,
  IUpdateSupplierRequest,
  IVarianceReport,
  SaleStatus,
} from './model/requests';

interface ICreateShopResponse {
  shop: IShopAccountRegister;
  token: ILoginAccessToken;
}
import { stringIsNothing } from '../utility/text';
import {
  cacheEvict,
  cacheEvictVariants,
  fetchWithCache,
} from './response-cache';

interface UserProfilePicture {
  pictureBase64: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  // Q.2 — set on read endpoints when the network failed and we
  // satisfied the request from the per-shop response cache. Consumers
  // can surface "last synced X ago" affordances without each page
  // reaching into the cache itself.
  stale?: boolean;
  lastSyncedUtc?: string;
  // Q.3 — set on createSale when the network was unavailable and the
  // sale was queued in the local outbox instead. data carries a
  // synthetic ISale with status='paid' so the cashier UI flow
  // continues normally; the sale lands on the server when the
  // network returns.
  pending?: boolean;
}

// Result of a PIN entry. Distinct from ApiResponse so a PIN surface can
// distinguish a wrong-PIN (401) from a lockout (429, with a countdown) and tell
// the operator how long to wait. Generic in its payload because the two PIN
// paths answer different questions - unlocking issues a token, verifying only
// says who the PIN belongs to - while failing in exactly the same ways, which
// is the part worth sharing.
export interface PinResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  status?: number;
  // Set on a 429 lockout: seconds until the operator can retry.
  lockedSeconds?: number;
}

export type PinUnlockResult = PinResult<ILoginAccessToken>;

// Who a staff PIN belongs to. No token, no session - see staff/verify-pin.
export interface IStaffPinIdentity {
  loginId: string;
  displayName: string;
  jobTitle?: string | null;
}

// The server's per-(shop, device) PIN lockout, as both PIN paths must read it.
// A 429 body carries a machine `error` code ("pin_locked") alongside the human
// `message`, so extractError would surface the code - hence this rather than
// the general path.
function pinFailure<T>(err: unknown): PinResult<T> {
  const ax = err as AxiosError;
  const status = ax.response?.status;
  if (status === 429) {
    const body = ax.response?.data as
      { message?: string; remainingSeconds?: number } | undefined;
    return {
      success: false,
      status,
      error: body?.message ?? 'Too many failed attempts. Try again shortly.',
      lockedSeconds: body?.remainingSeconds,
    };
  }
  return { success: false, status, error: extractError(err) };
}

// Single source of truth for extracting a human-readable error message
// from an axios failure. Order of preference:
//   1. RFC 7807 ProblemDetails: response body has `.detail` and `.title`
//      (content-type application/problem+json on the server side). Use
//      detail because it carries the specific cause; fall back to title.
//   2. Legacy string body — older endpoints return plain strings.
//   3. Axios error.message — covers transport failures (no response).
//   4. A generic fallback so we never return undefined.
//
// Importantly: never `String(object)` — that produces "[object Object]"
// which is what the previous coercion was doing.
function extractError(err: unknown): string {
  const error = err as AxiosError;
  const data = error.response?.data;
  if (data && typeof data === 'object') {
    const problem = data as {
      detail?: unknown;
      title?: unknown;
      error?: unknown;
    };
    if (typeof problem.detail === 'string' && problem.detail.length > 0)
      return problem.detail;
    if (typeof problem.title === 'string' && problem.title.length > 0)
      return problem.title;
    if (typeof problem.error === 'string' && problem.error.length > 0)
      return problem.error;
  }
  if (typeof data === 'string' && data.length > 0) return data;
  if (error?.message) return error.message;
  return 'Unknown error occurred';
}

export class EasyPosApiClient {
  private readonly axiosInstance: AxiosInstance;
  private _authToken?: string;
  public get authToken(): string | undefined {
    return this._authToken;
  }
  private readonly defaultTimeout: number = 30000; // 30 seconds default timeout

  constructor(baseUrl?: string) {
    const resolvedBaseUrl = baseUrl ?? defaultDomain ?? window.location.origin;
    this.axiosInstance = axios.create({
      baseURL: resolvedBaseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: this.defaultTimeout,
    });

    // 401 interceptor — a request that went out with a bearer token and
    // came back unauthorized means the server doesn't recognise the
    // token anymore (expired / revoked / data wiped under us in dev).
    // Fire a window event the app-shell listens for to tear down the
    // session and route to login. Skipped for requests that did NOT
    // carry an Authorization header (login / register / anonymous
    // shop-exists checks) — those legitimately return 401 on bad
    // credentials and the calling code surfaces the error itself.
    // Skipped for the /api/renew endpoint too, since renewUserToken()
    // already calls clearUserProfile() on failure; double-firing is
    // harmless but noisy.
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const sentAuth = !!error?.config?.headers?.Authorization;
        const url: string = error?.config?.url ?? '';
        const isRenew = url.includes('/api/renew');
        if (status === 401 && sentAuth && !isRenew) {
          window.dispatchEvent(new CustomEvent('auth-expired'));
        }
        return Promise.reject(error);
      }
    );
  }

  public setAuthToken(token: string) {
    this._authToken = token;
    this.axiosInstance.defaults.headers.common['Authorization'] =
      `Bearer ${this._authToken}`;
    return this;
  }

  // Q.2 — Wrap a GET-shaped endpoint with the response cache. The
  // fetcher does the actual axios call and returns the response payload.
  // On success: writes the payload to the per-shop cache + returns
  // {success, data}. On network failure: serves the most recent cache
  // entry with stale=true + lastSyncedUtc set; only reports {success:
  // false} when there's no cache to fall back on.
  private async cachedGet<T>(
    route: string,
    fetcher: () => Promise<T>,
    options: { shopName?: string; query?: unknown; ttlMs?: number } = {}
  ): Promise<ApiResponse<T>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const result = await fetchWithCache(route, fetcher, options);
      return {
        success: true,
        data: result.data,
        stale: result.fromCache || undefined,
        lastSyncedUtc: result.cachedAtUtc,
      };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async getServerVersion(): Promise<ApiResponse<string>> {
    try {
      const response = await this.axiosInstance.get<{ version: string }>(
        '/api/version'
      );
      // Strip the +sha build metadata. SemVer treats anything after `+` as
      // build info (not part of the comparable version), so `0.1.12+abc...`
      // and `0.1.12` are the same release. Both the skew check and the
      // About display only need the semver portion.
      const version = response.data.version.split('+')[0];
      return { success: true, data: version };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async login(
    emailAddress: string,
    password: string,
    twoFactorCode?: string
  ): Promise<ApiResponse<ILoginAccessToken>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<ILoginAccessToken>(
        '/api/login',
        {
          emailAddress,
          password,
          twoFactorCode,
        } as ILoginDetails
      );
      this.setAuthToken(response.data.encodedLoginAccessToken);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // PIN-unlock a manually-locked register. Anonymous (the lock cleared the
  // token) - identified by deviceId, which derives the shop server-side. On
  // success the returned token is a fresh login for the resolved staff member
  // (same shape as password login); if the device's open shift belonged to a
  // different cashier, the server has already handed it over (no recount).
  async pinUnlock(deviceId: string, pin: string): Promise<PinUnlockResult> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<ILoginAccessToken>(
        '/api/auth/pin-unlock',
        { deviceId, pin }
      );
      this.setAuthToken(response.data.encodedLoginAccessToken);
      return { success: true, data: response.data };
    } catch (err) {
      return pinFailure<ILoginAccessToken>(err);
    }
  }

  async register(
    emailAddress: string,
    password: string,
    displayName?: string
  ): Promise<ApiResponse<ILoginAccessToken>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<ILoginAccessToken>(
        '/api/register',
        {
          emailAddress,
          password,
          displayName,
        } as IRegisterDetails
      );
      this.setAuthToken(response.data.encodedLoginAccessToken);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async loginWithFacebook(
    accessToken: string
  ): Promise<ApiResponse<ILoginAccessToken>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<ILoginAccessToken>(
        '/api/login/facebook',
        {
          accessToken,
        } as ILoginWithExternalProvider
      );
      this.setAuthToken(response.data.encodedLoginAccessToken);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }
  async loginWithGoogleCode(
    details: ILoginWithGoogleCode
  ): Promise<ApiResponse<ILoginAccessToken>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<ILoginAccessToken>(
        '/api/login/google/code',
        details
      );
      this.setAuthToken(response.data.encodedLoginAccessToken);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async verify(token: string): Promise<ApiResponse<ILoginAccessToken>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<ILoginAccessToken>(
        `/api/verify?token=${token}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }
  async renew(): Promise<ApiResponse<ILoginAccessToken>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response =
        await this.axiosInstance.get<ILoginAccessToken>('/api/renew');
      this._authToken = response.data.encodedLoginAccessToken;
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }
  async myProfilePicture(): Promise<ApiResponse<string>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response =
        await this.axiosInstance.get<UserProfilePicture>('/api/login/picture');
      return {
        success: !stringIsNothing(response.data.pictureBase64),
        data: response.data.pictureBase64,
      };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Dev-only emergency reset: wipe a sample shop by name + reseed it
  // from SampleData. Server still requires the caller to own the shop
  // AND the shop to have isSample=true; the dev gate just makes the
  // endpoint discoverable. Returns 404 for non-dev callers.
  async devResetSampleShop(
    shopName: string
  ): Promise<ApiResponse<{ shopName: string }>> {
    // Reseeding does the same volume of writes as the public sandbox
    // create, so allow the same generous timeout.
    this.axiosInstance.defaults.timeout = Math.max(this.defaultTimeout, 30_000);
    try {
      const response = await this.axiosInstance.post<{ shopName: string }>(
        `/api/dev/shop/${encodeURIComponent(shopName)}/reset-sample`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    } finally {
      this.axiosInstance.defaults.timeout = this.defaultTimeout;
    }
  }

  // Owner-self-serve reset for a sample shop. Sets a fresh bearer
  // because the server renews the token to reflect the freshly-seeded
  // subscription snapshot.
  async resetSampleShop(
    shopName: string
  ): Promise<ApiResponse<ICreateShopResponse>> {
    this.axiosInstance.defaults.timeout = Math.max(this.defaultTimeout, 30_000);
    try {
      const response = await this.axiosInstance.post<ICreateShopResponse>(
        `/api/shop/${encodeURIComponent(shopName)}/reset-sample`
      );
      this.setAuthToken(response.data.token.encodedLoginAccessToken);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    } finally {
      this.axiosInstance.defaults.timeout = this.defaultTimeout;
    }
  }

  async devDeleteShop(shopName: string): Promise<ApiResponse<boolean>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      await this.axiosInstance.delete(
        `/api/dev/shop/${encodeURIComponent(shopName)}`
      );
      return { success: true, data: true };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Dev-only: flip a shop's subscription tier without going through a
  // payment processor. Server returns a refreshed login token so the PWA
  // can dispatch user-profile-set and have tier-gated UI re-render
  // immediately. Owner-gated server-side.
  async devSetShopTier(
    shopName: string,
    tier: 'free' | 'bronze' | 'silver' | 'gold'
  ): Promise<
    ApiResponse<{
      shopName: string;
      tier: string;
      status: string;
      allowsInventory: boolean;
      allowsCashManagement: boolean;
      token: ILoginAccessToken | null;
    }>
  > {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<{
        shopName: string;
        tier: string;
        status: string;
        allowsInventory: boolean;
        allowsCashManagement: boolean;
        token: ILoginAccessToken | null;
      }>(`/api/dev/shop/${encodeURIComponent(shopName)}/tier`, { tier });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async createShop(
    body: ICreateShopRequest
  ): Promise<ApiResponse<ICreateShopResponse>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<ICreateShopResponse>(
        '/api/shop',
        body
      );
      this.setAuthToken(response.data.token.encodedLoginAccessToken);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Create a personal sample shop seeded with sample data (locations,
  // suppliers, stock, categories, menu, published). Quota-checked
  // server-side: caller may own at most 1 sample shop at a time. The
  // resulting shop is rate-limited to 3 paid sales per UTC day.
  async createSampleShop(
    body: ICreateShopRequest
  ): Promise<ApiResponse<ICreateShopResponse>> {
    // Sample seeding does a lot of writes — allow more time than the
    // default before timeout fires.
    this.axiosInstance.defaults.timeout = Math.max(this.defaultTimeout, 30_000);
    try {
      const response = await this.axiosInstance.post<ICreateShopResponse>(
        '/api/shop/sample',
        body
      );
      this.setAuthToken(response.data.token.encodedLoginAccessToken);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    } finally {
      this.axiosInstance.defaults.timeout = this.defaultTimeout;
    }
  }

  async getShop(shopName: string): Promise<ApiResponse<IShopAccountRegister>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<IShopAccountRegister>(
        `/api/shop/${encodeURIComponent(shopName)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async enrollAsCustomer(): Promise<ApiResponse<ILoginAccessToken>> {
    try {
      const response = await this.axiosInstance.post<ILoginAccessToken>(
        '/api/customer/enroll'
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async getCustomerMemberships(): Promise<
    ApiResponse<IShopLoyaltyMembership[]>
  > {
    try {
      const response = await this.axiosInstance.get<IShopLoyaltyMembership[]>(
        '/api/customer/memberships'
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async joinShopByCode(
    code: string
  ): Promise<ApiResponse<IShopLoyaltyMembership>> {
    try {
      const response = await this.axiosInstance.post<IShopLoyaltyMembership>(
        '/api/loyalty/join',
        { code }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async lookupShopByCode(
    code: string
  ): Promise<ApiResponse<{ shopName: string; shopDisplayName: string }>> {
    try {
      const response = await this.axiosInstance.get<{
        shopName: string;
        shopDisplayName: string;
      }>(`/api/shop/by-code/${encodeURIComponent(code)}`);
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Operator-facing: resolve a member code (typed or scanned at the till) to
  // the member for THIS shop. 404 when unknown / foreign / inactive.
  async resolveMemberCode(
    shopName: string,
    code: string
  ): Promise<ApiResponse<IResolvedMember>> {
    try {
      const response = await this.axiosInstance.get<IResolvedMember>(
        `/api/shop/${encodeURIComponent(shopName)}/loyalty/resolve/${encodeURIComponent(code)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // ---- N.7.4 internal staff ------------------------------------------------
  //
  // Staff are internal to the shop: a membership row with a synthetic staff-{ulid} and no
  // Login. They never sign in. The device holds the shop session; a staff PIN says who is
  // working, for record keeping.

  async listStaff(shopName: string): Promise<ApiResponse<IStaffMember[]>> {
    try {
      const response = await this.axiosInstance.get<IStaffMember[]>(
        `/api/shop/${encodeURIComponent(shopName)}/members`
      );
      // The endpoint lists every member. Staff are the ones with a synthetic id; the owner and
      // any real logins keep their details on their own accounts and are not managed here.
      return {
        success: true,
        data: response.data.filter((m) => m.loginId.startsWith('staff-')),
      };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async addStaff(
    shopName: string,
    body: {
      displayName: string;
      pin: string;
      emailAddress?: string;
      phoneNumber?: string;
      jobTitle?: string;
      notes?: string;
    }
  ): Promise<ApiResponse<IStaffMember>> {
    try {
      const response = await this.axiosInstance.post<IStaffMember>(
        `/api/shop/${encodeURIComponent(shopName)}/staff`,
        body
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  /** Partial: a field left out is left alone, an empty string clears it. */
  async updateStaff(
    shopName: string,
    loginId: string,
    body: {
      displayName?: string;
      emailAddress?: string;
      phoneNumber?: string;
      jobTitle?: string;
      notes?: string;
      pin?: string;
      active?: boolean;
    }
  ): Promise<ApiResponse<IStaffMember>> {
    try {
      const response = await this.axiosInstance.put<IStaffMember>(
        `/api/shop/${encodeURIComponent(shopName)}/staff/${encodeURIComponent(loginId)}`,
        body
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async retireStaff(
    shopName: string,
    loginId: string
  ): Promise<ApiResponse<IStaffMember>> {
    try {
      const response = await this.axiosInstance.delete<IStaffMember>(
        `/api/shop/${encodeURIComponent(shopName)}/staff/${encodeURIComponent(loginId)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  /**
   * Who is at the till. Verifies a staff PIN and answers who it belongs to; issues nothing -
   * no token, no session, no change to the shift.
   */
  async verifyStaffPin(
    shopName: string,
    deviceId: string,
    pin: string
  ): Promise<PinResult<IStaffPinIdentity>> {
    try {
      const response = await this.axiosInstance.post<IStaffPinIdentity>(
        `/api/shop/${encodeURIComponent(shopName)}/staff/verify-pin`,
        { deviceId, pin }
      );
      return { success: true, data: response.data };
    } catch (err) {
      // Shares the lockout handling with pinUnlock: the same resolver enforces
      // the same per-(shop, device) lockout behind both, so the countdown must
      // read the same way at the till as it does on the lock screen.
      return pinFailure<IStaffPinIdentity>(err);
    }
  }

  // Operator-facing: the member "welcome" context for the till (store-credit
  // balance + recent purchases for last-visit / reorder).
  async getMemberTillContext(
    shopName: string,
    loginId: string
  ): Promise<ApiResponse<IMemberTillContext>> {
    try {
      const response = await this.axiosInstance.get<IMemberTillContext>(
        `/api/shop/${encodeURIComponent(shopName)}/members/${encodeURIComponent(loginId)}/till-context`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Operator-facing (P2c): resolve a store-credit note by its human code,
  // typed or scanned at the till. 404 when unknown / foreign to this shop.
  async resolveCreditNoteByCode(
    shopName: string,
    code: string
  ): Promise<ApiResponse<ICreditNote>> {
    try {
      const response = await this.axiosInstance.get<ICreditNote>(
        `/api/shop/${encodeURIComponent(shopName)}/credit-notes/by-code/${encodeURIComponent(code)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Operator-facing (P2c): fetch a store-credit note by its id (a scanned QR
  // encodes the id). 404 when unknown / foreign to this shop.
  async getCreditNote(
    shopName: string,
    id: string
  ): Promise<ApiResponse<ICreditNote>> {
    try {
      const response = await this.axiosInstance.get<ICreditNote>(
        `/api/shop/${encodeURIComponent(shopName)}/credit-notes/${encodeURIComponent(id)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Operator-facing (P2c): every store-credit note attached to a member plus
  // the spendable total right now - powers the till's credit picker list.
  async listCreditNotesByMember(
    shopName: string,
    loginId: string
  ): Promise<ApiResponse<ICreditNotesByMember>> {
    try {
      const response = await this.axiosInstance.get<ICreditNotesByMember>(
        `/api/shop/${encodeURIComponent(shopName)}/credit-notes/by-member/${encodeURIComponent(loginId)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Customer-facing: the authenticated member's own purchases at one shop.
  async getCustomerPurchases(
    shopName: string
  ): Promise<ApiResponse<ICustomerPurchase[]>> {
    try {
      const response = await this.axiosInstance.get<ICustomerPurchase[]>(
        `/api/customer/purchases`,
        { params: { shop: shopName } }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async updateShopSettings(
    shopName: string,
    body: IUpdateShopSettingsRequest
  ): Promise<ApiResponse<IShopAccountRegister>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.put<IShopAccountRegister>(
        `/api/shop/${encodeURIComponent(shopName)}/settings`,
        body
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async listStock(shopName: string): Promise<ApiResponse<IStockItem[]>> {
    return this.cachedGet<IStockItem[]>(
      'listStock',
      async () => {
        const response = await this.axiosInstance.get<IStockItem[]>(
          `/api/shop/${encodeURIComponent(shopName)}/stock`
        );
        return response.data;
      },
      { shopName }
    );
  }

  async getStockItem(
    shopName: string,
    id: string
  ): Promise<ApiResponse<IStockItem>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<IStockItem>(
        `/api/shop/${encodeURIComponent(shopName)}/stock/${encodeURIComponent(id)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async createStockItem(
    shopName: string,
    body: ICreateStockItemRequest
  ): Promise<ApiResponse<IStockItem>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<IStockItem>(
        `/api/shop/${encodeURIComponent(shopName)}/stock`,
        body
      );
      void cacheEvict('listStock', { shopName });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async updateStockItem(
    shopName: string,
    id: string,
    body: IUpdateStockItemRequest
  ): Promise<ApiResponse<IStockItem>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.put<IStockItem>(
        `/api/shop/${encodeURIComponent(shopName)}/stock/${encodeURIComponent(id)}`,
        body
      );
      void cacheEvict('listStock', { shopName });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async deactivateStockItem(
    shopName: string,
    id: string
  ): Promise<ApiResponse<IStockItem>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.delete<IStockItem>(
        `/api/shop/${encodeURIComponent(shopName)}/stock/${encodeURIComponent(id)}`
      );
      void cacheEvict('listStock', { shopName });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Replace the stock item's supplier-link list. Owner-only. Empty
  // links list clears every supplier; first entry becomes primary.
  // Server validates each supplierId resolves to an active Supplier
  // and rejects duplicates.
  async updateStockItemSupplierLinks(
    shopName: string,
    id: string,
    links: IStockItemSupplierLink[]
  ): Promise<ApiResponse<IStockItem>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.put<IStockItem>(
        `/api/shop/${encodeURIComponent(shopName)}/stock/${encodeURIComponent(id)}/supplier-links`,
        { links }
      );
      void cacheEvict('listStock', { shopName });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async listCategories(shopName: string): Promise<ApiResponse<ICategory[]>> {
    return this.cachedGet<ICategory[]>(
      'listCategories',
      async () => {
        const response = await this.axiosInstance.get<ICategory[]>(
          `/api/shop/${encodeURIComponent(shopName)}/categories`
        );
        return response.data;
      },
      { shopName }
    );
  }

  async replaceCategories(
    shopName: string,
    categories: ICategory[]
  ): Promise<ApiResponse<ICategory[]>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.put<ICategory[]>(
        `/api/shop/${encodeURIComponent(shopName)}/categories`,
        { categories }
      );
      void cacheEvict('listCategories', { shopName });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async listStockLocations(
    shopName: string
  ): Promise<ApiResponse<IStockLocation[]>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<IStockLocation[]>(
        `/api/shop/${encodeURIComponent(shopName)}/stocklocations`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async replaceStockLocations(
    shopName: string,
    locations: IStockLocation[]
  ): Promise<ApiResponse<IStockLocation[]>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.put<IStockLocation[]>(
        `/api/shop/${encodeURIComponent(shopName)}/stocklocations`,
        { locations }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async listMenu(shopName: string): Promise<ApiResponse<IProduct[]>> {
    return this.cachedGet<IProduct[]>(
      'listMenu',
      async () => {
        const response = await this.axiosInstance.get<IProduct[]>(
          `/api/shop/${encodeURIComponent(shopName)}/products`
        );
        return response.data;
      },
      { shopName }
    );
  }

  async getProduct(
    shopName: string,
    id: string
  ): Promise<ApiResponse<IProduct>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<IProduct>(
        `/api/shop/${encodeURIComponent(shopName)}/products/${encodeURIComponent(id)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async createProduct(
    shopName: string,
    body: ICreateProductRequest
  ): Promise<ApiResponse<IProduct>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<IProduct>(
        `/api/shop/${encodeURIComponent(shopName)}/products`,
        body
      );
      void cacheEvict('listMenu', { shopName });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async updateProduct(
    shopName: string,
    id: string,
    body: IUpdateProductRequest
  ): Promise<ApiResponse<IProduct>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.put<IProduct>(
        `/api/shop/${encodeURIComponent(shopName)}/products/${encodeURIComponent(id)}`,
        body
      );
      void cacheEvict('listMenu', { shopName });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async createSale(
    shopName: string,
    body: ICreateSaleRequest
  ): Promise<ApiResponse<ISale>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<ISale>(
        `/api/shop/${encodeURIComponent(shopName)}/sales`,
        body
      );
      return { success: true, data: response.data };
    } catch (err) {
      // Q.3 — network failure (no response received): queue the sale
      // locally and return a synthetic ISale so the cashier flow
      // continues. The drainer (state/pending-sales-store) replays
      // the queue when the connection returns. Server errors (status
      // ≥ 400) bubble through as today — those mean the payload was
      // bad, not that the network is down.
      if (axios.isAxiosError(err) && !err.response) {
        try {
          const { enqueueSale } = await import('../database/sale-outbox');
          const entry = await enqueueSale(shopName, body);
          const { pendingSalesStore } =
            await import('../state/pending-sales-store');
          void pendingSalesStore.refresh();
          return {
            success: true,
            pending: true,
            data: this._syntheticPendingSale(
              entry.idempotencyKey,
              entry.queuedAtUtc,
              body
            ),
          };
        } catch (queueErr) {
          // Outbox write itself failed (quota? IDB closed?). Surface
          // the original network error rather than masking it.
          void queueErr;
          return { success: false, error: extractError(err) };
        }
      }
      return { success: false, error: extractError(err) };
    }
  }

  // Build a synthetic ISale for the cashier flow when the real one is
  // queued offline. Status is 'paid' so the active-orders board sees
  // it; line snapshots are computed-light (PWA already has menu item
  // info to render). The server overwrites with the canonical record
  // when the queue drains.
  private _syntheticPendingSale(
    id: string,
    paidUtc: string,
    body: ICreateSaleRequest
  ): ISale {
    return {
      id,
      source: 'in_store',
      status: 'paid',
      // Synthetic placeholder (computed-light, total 0) — the server overwrites
      // with the canonical tenders when the outbox drains. Mirrors the request's
      // tenders; amounts are 0 here (the real total isn't known offline).
      tenders: body.tenders.map((t) => ({
        method: t.method,
        amount: t.amount ?? 0,
        reference: t.reference ?? null,
      })),
      displayMethod: body.tenders.map((t) => t.method).join(' + '),
      cashierId: null,
      pickupCode: body.pickupCode ?? null,
      tableTag: body.tableTag ?? null,
      lines: body.lines.map((l) => ({
        productId: l.productId,
        quantity: l.quantity,
        cellId: l.cellId ?? 'default',
        nameSnapshot: l.productId,
        priceSnapshot: 0,
        selectedModifierGroups: l.selectedModifierGroups ?? [],
        recipe: [],
      })),
      subtotal: 0,
      tax: 0,
      lineDiscountTotal: 0,
      cartDiscountAmount: 0,
      total: 0,
      consumptionApplied: false,
      // Phase Y — pending offline sales have no server-stamped sequence
      // or token yet; the synthetic surface uses 0 / "" so the renderer
      // hides both. The real values land when the server drains the
      // outbox and the next list-by-date fetch replaces the synthetic
      // record with the canonical one.
      daySequence: 0,
      trackToken: '',
      createdUtc: paidUtc,
      paidUtc,
      preparedUtc: null,
      completedUtc: null,
    };
  }

  async getSale(
    shopName: string,
    id: string,
    dateYyyyMmDd?: string
  ): Promise<ApiResponse<ISale>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<ISale>(
        `/api/shop/${encodeURIComponent(shopName)}/sales/${encodeURIComponent(id)}`,
        { params: dateYyyyMmDd ? { date: dateYyyyMmDd } : undefined }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async listSales(
    shopName: string,
    dateYyyyMmDd?: string
  ): Promise<ApiResponse<ISale[]>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<ISale[]>(
        `/api/shop/${encodeURIComponent(shopName)}/sales`,
        { params: dateYyyyMmDd ? { date: dateYyyyMmDd } : undefined }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async transitionSaleStatus(
    shopName: string,
    id: string,
    status: SaleStatus
  ): Promise<ApiResponse<ISale>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<ISale>(
        `/api/shop/${encodeURIComponent(shopName)}/sales/${encodeURIComponent(id)}/status`,
        { status }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Cancel an in-flight order. Defaults to refunding the customer; the
  // operator can skip the refund via the body. Completed orders are not
  // cancellable — refund them instead.
  async cancelSale(
    shopName: string,
    id: string,
    body: ICancelSaleRequest
  ): Promise<ApiResponse<ICancelSaleResult>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<ICancelSaleResult>(
        `/api/shop/${encodeURIComponent(shopName)}/sales/${encodeURIComponent(id)}/cancel`,
        body
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async listActiveOrders(
    shopName: string
  ): Promise<ApiResponse<IActiveOrderSummary[]>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<IActiveOrderSummary[]>(
        `/api/shop/${encodeURIComponent(shopName)}/orders/active`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // === Cash management (B1 + B1.5 — device-locked) ===
  // The drawer is 1:1 with the calling device. getMyRegister is the
  // primary read; open/close drive the per-device lifecycle.

  async listCashDrawers(shopName: string): Promise<ApiResponse<ICashDrawer[]>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<ICashDrawer[]>(
        `/api/shop/${encodeURIComponent(shopName)}/cash/drawers`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Q.2 - cached, because a cashier with no signal still needs to know whether
  // their till is open and what it is holding. On a network failure this serves
  // the last known register with `stale` set; the page renders it as last-known
  // rather than inventing a state. Reporting a register as CLOSED because the
  // server could not be reached is the one answer that must never be given: it
  // invites the cashier to open a second register on a device that has one.
  //
  // Keyed on the shop alone, deliberately. The cache lives in THIS device's
  // IndexedDB and `deviceId` is constant for the life of that storage, so it
  // cannot discriminate between two entries here - including it would only make
  // the cache harder to evict from the mutations below, which do not all carry
  // a deviceId.
  async getMyRegister(
    shopName: string,
    deviceId: string
  ): Promise<ApiResponse<IMyRegisterResponse>> {
    return this.cachedGet<IMyRegisterResponse>(
      'getMyRegister',
      async () => {
        const response = await this.axiosInstance.get<IMyRegisterResponse>(
          `/api/shop/${encodeURIComponent(shopName)}/cash/register`,
          { params: { deviceId } }
        );
        return response.data;
      },
      { shopName }
    );
  }

  // === Phase J2 — Shift lifecycle ===
  // Starting a shift opens the trading day (if first) + the cash session (when
  // cash is on); ending it closes them (last shift end closes the day). The
  // manual day/open + day/close routes were removed — the day is shift-driven.

  async startShift(
    shopName: string,
    body: IStartShiftRequest
  ): Promise<ApiResponse<IShift>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<IShift>(
        `/api/shop/${encodeURIComponent(shopName)}/shift/start`,
        body
      );
      // The register this shift owns has changed shape; drop the cached read.
      void cacheEvict('getMyRegister', { shopName });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async endShift(
    shopName: string,
    body: IEndShiftRequest
  ): Promise<ApiResponse<IShift>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<IShift>(
        `/api/shop/${encodeURIComponent(shopName)}/shift/end`,
        body
      );
      // The register this shift owns has changed shape; drop the cached read.
      void cacheEvict('getMyRegister', { shopName });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async handOverShift(
    shopName: string,
    body: IHandOverShiftRequest
  ): Promise<ApiResponse<IHandOverResult>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<IHandOverResult>(
        `/api/shop/${encodeURIComponent(shopName)}/shift/handover`,
        body
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // The open shift on a device, or { open: false }. The day-bar / till
  // "are you on a shift?" read for both cash modes.
  async getCurrentShift(
    shopName: string,
    deviceId: string
  ): Promise<ApiResponse<ICurrentShift>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<ICurrentShift>(
        `/api/shop/${encodeURIComponent(shopName)}/shift/current`,
        { params: { deviceId } }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async listShifts(
    shopName: string,
    opts?: { dayId?: string; deviceId?: string; from?: string; to?: string }
  ): Promise<ApiResponse<IShift[]>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const params: Record<string, string> = {};
      if (opts?.dayId) params.dayId = opts.dayId;
      if (opts?.deviceId) params.deviceId = opts.deviceId;
      if (opts?.from) params.from = opts.from;
      if (opts?.to) params.to = opts.to;
      const response = await this.axiosInstance.get<IShift[]>(
        `/api/shop/${encodeURIComponent(shopName)}/shifts`,
        { params: Object.keys(params).length ? params : undefined }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // === Phase D — ShopDay trading-day READS (open/close are shift-driven) ===

  async getCurrentDay(shopName: string): Promise<ApiResponse<ICurrentDay>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<ICurrentDay>(
        `/api/shop/${encodeURIComponent(shopName)}/day/current`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async getDay(
    shopName: string,
    dayId: string
  ): Promise<ApiResponse<IShopDay>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<IShopDay>(
        `/api/shop/${encodeURIComponent(shopName)}/day/${encodeURIComponent(dayId)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Recent trading days, newest first (includes the current open/closing day).
  // Backs the daily Z-report's recent-days picker.
  async listDays(
    shopName: string,
    limit?: number
  ): Promise<ApiResponse<IShopDay[]>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const params: Record<string, string> = {};
      if (limit != null) params.limit = String(limit);
      const response = await this.axiosInstance.get<IShopDay[]>(
        `/api/shop/${encodeURIComponent(shopName)}/days`,
        { params: Object.keys(params).length ? params : undefined }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // The shop-wide daily Z-report for one trading day (owner-only). A finalised
  // day returns the immutable summary; a still-open day returns a partial live
  // running total (partial=true).
  async getDayReport(
    shopName: string,
    dayId: string
  ): Promise<ApiResponse<IDayReport>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<IDayReport>(
        `/api/shop/${encodeURIComponent(shopName)}/reports/day/${encodeURIComponent(dayId)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // J2: opening/closing a cash session folds into startShift / endShift above.

  async getCashSession(
    shopName: string,
    sessionId: string
  ): Promise<ApiResponse<ICashSession>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<ICashSession>(
        `/api/shop/${encodeURIComponent(shopName)}/cash/sessions/${encodeURIComponent(sessionId)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async forceDrainCashSession(
    shopName: string,
    sessionId: string
  ): Promise<ApiResponse<{ drained: number }>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<{ drained: number }>(
        `/api/shop/${encodeURIComponent(shopName)}/cash/sessions/${encodeURIComponent(sessionId)}/drain`
      );
      // Movements changed what the drawer holds; drop the cached read.
      void cacheEvict('getMyRegister', { shopName });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async recordCashMovement(
    shopName: string,
    sessionId: string,
    body: IRecordManualMovementRequest
  ): Promise<ApiResponse<ICashSession>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<ICashSession>(
        `/api/shop/${encodeURIComponent(shopName)}/cash/sessions/${encodeURIComponent(sessionId)}/movement`,
        body
      );
      // Movements changed what the drawer holds; drop the cached read.
      void cacheEvict('getMyRegister', { shopName });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // === Refunds (D2) ===
  async createRefund(
    shopName: string,
    body: ICreateRefundRequest
  ): Promise<ApiResponse<IRefund>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<IRefund>(
        `/api/shop/${encodeURIComponent(shopName)}/refunds`,
        body
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async listRefunds(
    shopName: string,
    date?: string
  ): Promise<ApiResponse<IRefund[]>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<IRefund[]>(
        `/api/shop/${encodeURIComponent(shopName)}/refunds`,
        { params: date ? { date } : undefined }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async listRefundsForSale(
    shopName: string,
    saleId: string,
    saleDate?: string
  ): Promise<ApiResponse<IRefund[]>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<IRefund[]>(
        `/api/shop/${encodeURIComponent(shopName)}/sales/${encodeURIComponent(saleId)}/refunds`,
        { params: saleDate ? { saleDate } : undefined }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // B3 — sessions list.
  async listCashSessions(
    shopName: string,
    opts?: { from?: string; to?: string; deviceId?: string }
  ): Promise<ApiResponse<ICashSession[]>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<ICashSession[]>(
        `/api/shop/${encodeURIComponent(shopName)}/cash/sessions`,
        { params: opts }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async getZReport(
    shopName: string,
    sessionId: string
  ): Promise<ApiResponse<IZReport>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<IZReport>(
        `/api/shop/${encodeURIComponent(shopName)}/cash/sessions/${encodeURIComponent(sessionId)}/z-report`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async reconcileCashSession(
    shopName: string,
    sessionId: string
  ): Promise<ApiResponse<ICashSessionReconcileReport>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response =
        await this.axiosInstance.get<ICashSessionReconcileReport>(
          `/api/shop/${encodeURIComponent(shopName)}/cash/sessions/${encodeURIComponent(sessionId)}/reconcile`
        );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Phase Y.B1 — public order tracking. Anonymous endpoint; the auth
  // header is stripped for this call (the page may be loaded inside
  // the operator PWA after they scanned their own QR — we don't want
  // the operator bearer to confuse the public surface).
  async getTrackedSale(
    token: string
  ): Promise<ApiResponse<ITrackedSaleResponse>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    const prevAuth =
      this.axiosInstance.defaults.headers.common['Authorization'];
    try {
      delete this.axiosInstance.defaults.headers.common['Authorization'];
      const response = await this.axiosInstance.get<ITrackedSaleResponse>(
        `/api/track/${encodeURIComponent(token)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    } finally {
      if (prevAuth) {
        this.axiosInstance.defaults.headers.common['Authorization'] = prevAuth;
      }
    }
  }

  // Y.C2 — anonymous customer-side completion. Posts an optional
  // 1-5 star rating and transitions the sale to Completed; mirrors
  // getTrackedSale's auth-header stripping so the operator's bearer
  // (when present in the same browser) isn't sent with a public POST.
  async confirmTrackedSale(
    token: string,
    rating?: number | null
  ): Promise<ApiResponse<ITrackedSaleResponse>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    const prevAuth =
      this.axiosInstance.defaults.headers.common['Authorization'];
    try {
      delete this.axiosInstance.defaults.headers.common['Authorization'];
      const body = rating != null ? { rating } : {};
      const response = await this.axiosInstance.post<ITrackedSaleResponse>(
        `/api/track/${encodeURIComponent(token)}/confirm`,
        body
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    } finally {
      if (prevAuth) {
        this.axiosInstance.defaults.headers.common['Authorization'] = prevAuth;
      }
    }
  }

  async deactivateProduct(
    shopName: string,
    id: string
  ): Promise<ApiResponse<IProduct>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.delete<IProduct>(
        `/api/shop/${encodeURIComponent(shopName)}/products/${encodeURIComponent(id)}`
      );
      void cacheEvict('listMenu', { shopName });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Uploads a JPEG (or PNG / SVG) for a menu item. The crop component on the
  // client takes care of resizing to ~600x600 before posting; the server caps
  // payload at 1MB. Returns the updated menu item with a fresh SAS image URL.
  async uploadProductImage(
    shopName: string,
    id: string,
    bytes: Blob,
    contentType: string
  ): Promise<ApiResponse<IProduct>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<IProduct>(
        `/api/shop/${encodeURIComponent(shopName)}/products/${encodeURIComponent(id)}/image`,
        bytes,
        { headers: { 'Content-Type': contentType } }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async deleteProductImage(
    shopName: string,
    id: string
  ): Promise<ApiResponse<IProduct>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.delete<IProduct>(
        `/api/shop/${encodeURIComponent(shopName)}/products/${encodeURIComponent(id)}/image`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async listStockMovements(
    shopName: string,
    id: string,
    days?: number
  ): Promise<ApiResponse<IStockMovement[]>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<IStockMovement[]>(
        `/api/shop/${encodeURIComponent(shopName)}/stock/${encodeURIComponent(id)}/movements`,
        { params: days ? { days } : undefined }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async listStockTakes(
    shopName: string,
    months?: number
  ): Promise<ApiResponse<IStockTake[]>> {
    return this.cachedGet<IStockTake[]>(
      'listStockTakes',
      async () => {
        const response = await this.axiosInstance.get<IStockTake[]>(
          `/api/shop/${encodeURIComponent(shopName)}/stocktake`,
          { params: months ? { months } : undefined }
        );
        return response.data;
      },
      { shopName, query: { months } }
    );
  }

  async getStockTake(
    shopName: string,
    id: string
  ): Promise<ApiResponse<IStockTake>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<IStockTake>(
        `/api/shop/${encodeURIComponent(shopName)}/stocktake/${encodeURIComponent(id)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async startStockTake(
    shopName: string,
    body: IStartStockTakeRequest = {}
  ): Promise<ApiResponse<IStockTake>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<IStockTake>(
        `/api/shop/${encodeURIComponent(shopName)}/stocktake`,
        body
      );
      void cacheEvict('listStockTakes', { shopName });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async recordStockTakeCount(
    shopName: string,
    takeId: string,
    stockItemId: string,
    body: IRecordStockTakeCountRequest
  ): Promise<ApiResponse<IStockTake>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.put<IStockTake>(
        `/api/shop/${encodeURIComponent(shopName)}/stocktake/${encodeURIComponent(takeId)}/lines/${encodeURIComponent(stockItemId)}`,
        body
      );
      void cacheEvict('listStockTakes', { shopName });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async completeStockTake(
    shopName: string,
    id: string
  ): Promise<ApiResponse<ICompleteStockTakeResponse>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response =
        await this.axiosInstance.post<ICompleteStockTakeResponse>(
          `/api/shop/${encodeURIComponent(shopName)}/stocktake/${encodeURIComponent(id)}/complete`
        );
      void cacheEvict('listStockTakes', { shopName });
      void cacheEvict('listStock', { shopName }); // OnHand changes
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async cancelStockTake(
    shopName: string,
    id: string
  ): Promise<ApiResponse<IStockTake>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<IStockTake>(
        `/api/shop/${encodeURIComponent(shopName)}/stocktake/${encodeURIComponent(id)}/cancel`
      );
      void cacheEvict('listStockTakes', { shopName });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // ---- Sub-phase P: Suppliers ----
  async listSuppliers(
    shopName: string,
    includeInactive = false
  ): Promise<ApiResponse<ISupplier[]>> {
    return this.cachedGet<ISupplier[]>(
      'listSuppliers',
      async () => {
        const response = await this.axiosInstance.get<ISupplier[]>(
          `/api/shop/${encodeURIComponent(shopName)}/suppliers`,
          { params: includeInactive ? { includeInactive: 'true' } : undefined }
        );
        return response.data;
      },
      { shopName, query: { includeInactive } }
    );
  }

  async getSupplier(
    shopName: string,
    id: string
  ): Promise<ApiResponse<ISupplier>> {
    try {
      const response = await this.axiosInstance.get<ISupplier>(
        `/api/shop/${encodeURIComponent(shopName)}/suppliers/${encodeURIComponent(id)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async createSupplier(
    shopName: string,
    body: ICreateSupplierRequest
  ): Promise<ApiResponse<ISupplier>> {
    try {
      const response = await this.axiosInstance.post<ISupplier>(
        `/api/shop/${encodeURIComponent(shopName)}/suppliers`,
        body
      );
      void cacheEvictVariants(
        'listSuppliers',
        [{ includeInactive: false }, { includeInactive: true }],
        { shopName }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async updateSupplier(
    shopName: string,
    id: string,
    body: IUpdateSupplierRequest
  ): Promise<ApiResponse<ISupplier>> {
    try {
      const response = await this.axiosInstance.put<ISupplier>(
        `/api/shop/${encodeURIComponent(shopName)}/suppliers/${encodeURIComponent(id)}`,
        body
      );
      void cacheEvictVariants(
        'listSuppliers',
        [{ includeInactive: false }, { includeInactive: true }],
        { shopName }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async deactivateSupplier(
    shopName: string,
    id: string
  ): Promise<ApiResponse<ISupplier>> {
    try {
      const response = await this.axiosInstance.delete<ISupplier>(
        `/api/shop/${encodeURIComponent(shopName)}/suppliers/${encodeURIComponent(id)}`
      );
      void cacheEvictVariants(
        'listSuppliers',
        [{ includeInactive: false }, { includeInactive: true }],
        { shopName }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // ---- Sub-phase P: Purchase orders ----
  async listPurchaseOrders(
    shopName: string,
    options?: { months?: number; status?: string }
  ): Promise<ApiResponse<IPurchaseOrder[]>> {
    try {
      const response = await this.axiosInstance.get<IPurchaseOrder[]>(
        `/api/shop/${encodeURIComponent(shopName)}/purchase-orders`,
        { params: options }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async getPurchaseOrder(
    shopName: string,
    id: string
  ): Promise<ApiResponse<IPurchaseOrder>> {
    try {
      const response = await this.axiosInstance.get<IPurchaseOrder>(
        `/api/shop/${encodeURIComponent(shopName)}/purchase-orders/${encodeURIComponent(id)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async createPurchaseOrder(
    shopName: string,
    body: ICreatePurchaseOrderRequest
  ): Promise<ApiResponse<IPurchaseOrder>> {
    try {
      const response = await this.axiosInstance.post<IPurchaseOrder>(
        `/api/shop/${encodeURIComponent(shopName)}/purchase-orders`,
        body
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async updatePurchaseOrder(
    shopName: string,
    id: string,
    body: IUpdatePurchaseOrderRequest
  ): Promise<ApiResponse<IPurchaseOrder>> {
    try {
      const response = await this.axiosInstance.put<IPurchaseOrder>(
        `/api/shop/${encodeURIComponent(shopName)}/purchase-orders/${encodeURIComponent(id)}`,
        body
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async placePurchaseOrder(
    shopName: string,
    id: string
  ): Promise<ApiResponse<IPurchaseOrder>> {
    try {
      const response = await this.axiosInstance.post<IPurchaseOrder>(
        `/api/shop/${encodeURIComponent(shopName)}/purchase-orders/${encodeURIComponent(id)}/place`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async cancelPurchaseOrder(
    shopName: string,
    id: string,
    body: ICancelPurchaseOrderRequest = {}
  ): Promise<ApiResponse<IPurchaseOrder>> {
    try {
      const response = await this.axiosInstance.post<IPurchaseOrder>(
        `/api/shop/${encodeURIComponent(shopName)}/purchase-orders/${encodeURIComponent(id)}/cancel`,
        body
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // P.7 reorder helper. Owner-only. Drafts one PO per primary supplier
  // for every active stock item at-or-below its reorder level with a
  // valid reorderQty + linked active supplier. Returns drafted PO ids
  // + skipped items with reasons.
  async draftPurchaseOrdersFromLowStock(
    shopName: string
  ): Promise<ApiResponse<IDraftFromLowStockResult>> {
    try {
      const response = await this.axiosInstance.post<IDraftFromLowStockResult>(
        `/api/shop/${encodeURIComponent(shopName)}/purchase-orders/draft-from-low-stock`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // ---- Sub-phase P: Deliveries ----
  async listDeliveries(
    shopName: string,
    options?: { months?: number; status?: string }
  ): Promise<ApiResponse<IDelivery[]>> {
    try {
      const response = await this.axiosInstance.get<IDelivery[]>(
        `/api/shop/${encodeURIComponent(shopName)}/deliveries`,
        { params: options }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async getDelivery(
    shopName: string,
    id: string
  ): Promise<ApiResponse<IDelivery>> {
    try {
      const response = await this.axiosInstance.get<IDelivery>(
        `/api/shop/${encodeURIComponent(shopName)}/deliveries/${encodeURIComponent(id)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async createDelivery(
    shopName: string,
    body: ICreateDeliveryRequest
  ): Promise<ApiResponse<IDelivery>> {
    try {
      const response = await this.axiosInstance.post<IDelivery>(
        `/api/shop/${encodeURIComponent(shopName)}/deliveries`,
        body
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async updateDelivery(
    shopName: string,
    id: string,
    body: IUpdateDeliveryRequest
  ): Promise<ApiResponse<IDelivery>> {
    try {
      const response = await this.axiosInstance.put<IDelivery>(
        `/api/shop/${encodeURIComponent(shopName)}/deliveries/${encodeURIComponent(id)}`,
        body
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async cancelDelivery(
    shopName: string,
    id: string
  ): Promise<ApiResponse<IDelivery>> {
    try {
      const response = await this.axiosInstance.post<IDelivery>(
        `/api/shop/${encodeURIComponent(shopName)}/deliveries/${encodeURIComponent(id)}/cancel`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async confirmDelivery(
    shopName: string,
    id: string
  ): Promise<ApiResponse<IConfirmDeliveryResponse>> {
    try {
      const response = await this.axiosInstance.post<IConfirmDeliveryResponse>(
        `/api/shop/${encodeURIComponent(shopName)}/deliveries/${encodeURIComponent(id)}/confirm`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async salesReport(
    shopName: string,
    from?: string,
    to?: string
  ): Promise<ApiResponse<ISalesReport>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const params: Record<string, string> = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const response = await this.axiosInstance.get<ISalesReport>(
        `/api/shop/${encodeURIComponent(shopName)}/reports/sales`,
        { params: Object.keys(params).length ? params : undefined }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async lowStockReport(
    shopName: string
  ): Promise<ApiResponse<ILowStockReport>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<ILowStockReport>(
        `/api/shop/${encodeURIComponent(shopName)}/reports/low-stock`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async varianceReport(
    shopName: string,
    takeId: string
  ): Promise<ApiResponse<IVarianceReport>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<IVarianceReport>(
        `/api/shop/${encodeURIComponent(shopName)}/reports/variance/${encodeURIComponent(takeId)}`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async logout(): Promise<ApiResponse<string>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<string>('/api/logout');
      this._authToken = undefined;
      delete this.axiosInstance.defaults.headers.common['Authorization'];
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Item availability — operational overlay flipped by staff when an item
  // runs out. Server publishes productAvailabilityChanged on every flip.
  async listProductAvailability(
    shopName: string
  ): Promise<ApiResponse<IProductAvailability[]>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<IProductAvailability[]>(
        `/api/shop/${encodeURIComponent(shopName)}/product-availability`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async setProductUnavailable(
    shopName: string,
    itemId: string,
    body: ISetProductUnavailableRequest
  ): Promise<ApiResponse<IProductAvailability>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<IProductAvailability>(
        `/api/shop/${encodeURIComponent(shopName)}/product-availability/${encodeURIComponent(itemId)}`,
        body
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async clearProductUnavailable(
    shopName: string,
    itemId: string
  ): Promise<ApiResponse<void>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      await this.axiosInstance.delete(
        `/api/shop/${encodeURIComponent(shopName)}/product-availability/${encodeURIComponent(itemId)}`
      );
      return { success: true };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Editorial → published bundle. The status endpoint backs the
  // "publish required" pill in the menu admin page; publishMenu performs
  // the snapshot. Server publishes a `menuPublished` realtime event on
  // success (best-effort).
  async getPublishStatus(
    shopName: string
  ): Promise<ApiResponse<IPublishStatus>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.get<IPublishStatus>(
        `/api/shop/${encodeURIComponent(shopName)}/published-status`
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async publishMenu(
    shopName: string
  ): Promise<ApiResponse<IPublishMenuResponse>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<IPublishMenuResponse>(
        `/api/shop/${encodeURIComponent(shopName)}/products/publish`,
        {}
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Realtime negotiate — issues SignalR client connection info bound to the
  // caller's loginId. Server returns 503 when AzureSignalRConnectionString
  // is not configured (dev environments without a real SignalR resource).
  //
  // The optional body (Phase N onwards) carries deviceId + mode so the
  // server can touch the DeviceRegistration freshness signal and, in
  // Phase O, enforce subscription caps. Sending an empty body still
  // works — the server tolerates that case.
  async realtimeNegotiate(body?: IRealtimeNegotiateRequest): Promise<
    ApiResponse<IRealtimeNegotiateResponse> & {
      tierBlocked?: IRealtimeTierBlocked;
    }
  > {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response =
        await this.axiosInstance.post<IRealtimeNegotiateResponse>(
          '/api/realtime/negotiate',
          body ?? {}
        );
      return { success: true, data: response.data };
    } catch (err) {
      // N.7a — server returns a structured 403 when the shop's tier
      // doesn't unlock realtime. Surface the typed body so the
      // realtime-connection can switch into a "polling-only" mode
      // rather than treating it as a hard failure.
      if (axios.isAxiosError(err) && err.response?.status === 403) {
        const body = err.response.data as
          Partial<IRealtimeTierBlocked> | undefined;
        if (body && body.reason === 'tier_requires_upgrade') {
          return {
            success: false,
            error: body.message ?? 'Realtime requires an upgrade.',
            tierBlocked: {
              reason: body.reason,
              currentTier: body.currentTier ?? 'free',
              requiredTier: body.requiredTier ?? 'bronze',
              message: body.message ?? 'Realtime requires an upgrade.',
            },
          };
        }
      }
      return { success: false, error: extractError(err) };
    }
  }

  // Owner-only: register / update a device for a shop. The body shape
  // matches the server's UpsertDeviceRequest. Used by the shop-settings
  // device-binding section.
  async upsertDevice(
    shopName: string,
    deviceId: string,
    body: IUpsertDeviceRequest
  ): Promise<ApiResponse<IDeviceRegistration>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.put<IDeviceRegistration>(
        `/api/shop/${encodeURIComponent(shopName)}/devices/${encodeURIComponent(deviceId)}`,
        body
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Cross-shop lookup: is THIS deviceId bound exclusively to a shop?
  // Returns { kind: 'personal' } when the device is unbound, or
  // { kind: 'shop-owned', shopName, allowedModes, defaultMode, ... } when
  // an owner has bound it. Called once per session at startup so the PWA
  // can route the user into the correct shop or reject them if they're
  // not a member.
  async getDeviceBinding(
    deviceId: string
  ): Promise<ApiResponse<IDeviceBindingResponse>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<IDeviceBindingResponse>(
        '/api/me/device-binding',
        { deviceId }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async realtimeJoinShopGroup(
    shopName: string
  ): Promise<ApiResponse<IJoinShopGroupResponse>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      const response = await this.axiosInstance.post<IJoinShopGroupResponse>(
        '/api/realtime/joinShopGroup',
        { shopName }
      );
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  async realtimeLeaveShopGroup(shopName: string): Promise<ApiResponse<void>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      await this.axiosInstance.post('/api/realtime/leaveShopGroup', {
        shopName,
      });
      return { success: true };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }

  // Dev-only smoke test — server enforces token.IsDevAccount. Returns 403 for
  // non-dev users, 503 when realtime is not configured.
  async realtimePing(
    shopName: string,
    message?: string
  ): Promise<ApiResponse<void>> {
    this.axiosInstance.defaults.timeout = this.defaultTimeout;
    try {
      await this.axiosInstance.post('/api/realtime/ping', {
        shopName,
        message,
      });
      return { success: true };
    } catch (err) {
      return { success: false, error: extractError(err) };
    }
  }
}

export interface IRealtimeNegotiateRequest {
  shopName?: string;
  deviceId?: string;
  mode?: string;
}

export interface IRealtimeNegotiateResponse {
  url: string;
  accessToken: string;
  hub: string;
  userId: string;
  deviceId?: string | null;
  mode?: string | null;
}

// Phase Y.B1 — public order tracking endpoint response. Customer-
// facing summary only; never carries PII even though the server-side
// Sale has contact fields. Lines carry the menu item name + quantity
// + a flat list of selected-option labels (no prices, no recipe).
export interface ITrackedSaleResponse {
  token: string;
  shopDisplayName: string;
  daySequence: number;
  status: SaleStatus;
  paidUtc: string;
  ageSeconds: number;
  lines: Array<{ name: string; quantity: number; options: string[] }>;
  isClosed: boolean;
}

// Server response body when negotiate is refused on tier-gated grounds
// (N.7a). PWA branches on `reason` and renders an amber tier-blocked
// banner instead of treating it as a connection failure.
export interface IRealtimeTierBlocked {
  reason: 'tier_requires_upgrade';
  currentTier: string;
  requiredTier: string;
  message: string;
}

export interface IJoinShopGroupResponse {
  joined: string[];
}

// Server's DeviceRegistration shape (mirrors
// Server/EasyposServer/Entities/System/DeviceRegistration.cs).
export interface IDeviceRegistration {
  shopName: string;
  deviceId: string;
  displayName: string;
  allowedModes: string[];
  defaultMode: string;
  registeredByLoginId: string;
  registeredUtc: string;
  lastSeenUtc: string;
  lastUserLoginId?: string | null;
  status: 'active' | 'deregistered';
  isExclusiveToShop: boolean;
}

export interface IUpsertDeviceRequest {
  displayName: string;
  allowedModes: string[];
  defaultMode: string;
  isExclusiveToShop?: boolean;
}

// Response from POST /api/me/device-binding. When kind === 'personal'
// the *Shop fields are absent. When kind === 'shop-owned' the device
// is bound to ShopName; PWA routes accordingly.
export interface IDeviceBindingResponse {
  kind: 'personal' | 'shop-owned';
  shopName?: string;
  displayName?: string;
  allowedModes?: string[];
  defaultMode?: string;
  registeredByLoginId?: string;
  registeredUtc?: string;
}

export type ProductAvailabilityReason =
  'out-of-stock' | 'equipment-down' | 'service-window' | 'custom';

export interface IProductAvailability {
  itemId: string;
  isUnavailable: boolean;
  sinceUtc: string;
  byLoginId: string;
  reason: ProductAvailabilityReason;
  reasonNote?: string | null;
  unavailableUntilUtc?: string | null;
}

export interface ISetProductUnavailableRequest {
  reason: ProductAvailabilityReason;
  reasonNote?: string;
  unavailableUntilUtc?: string;
}

export interface IProductAvailabilityChangedEvent {
  itemId: string;
  isUnavailable: boolean;
  sinceUtc?: string;
  byLoginId?: string;
  reason?: ProductAvailabilityReason;
  reasonNote?: string | null;
  unavailableUntilUtc?: string | null;
}

// Editorial vs published bundle status. publishRequired is true whenever
// editorialVersion > publishedVersion. hasEverPublished is false on a
// freshly-created shop (PWA can render "Never published" instead of
// "Up to date" in that case).
export interface IPublishStatus {
  editorialVersion: number;
  publishedVersion: number;
  lastEditedUtc?: string | null;
  lastPublishedUtc?: string | null;
  publishRequired: boolean;
  hasEverPublished: boolean;
}

export interface IPublishedMenuPointer {
  version: number;
  blobPath: string;
  blobETag: string;
  publishedUtc: string;
}

export interface IPublishMenuResponse {
  wasNoOp: boolean;
  pointer: IPublishedMenuPointer;
}

// Shape of the menuPublished realtime event broadcast to the shop's
// realtime group on a successful publish. Mirrors PublishedMenuPointer
// + adds publishedUtc as the source of the change.
export interface IMenuPublishedEvent {
  version: number;
  blobPath: string;
  etag: string;
  publishedUtc: string;
}

export class ApiFactory {
  public static instance: EasyPosApiClient = new EasyPosApiClient();
  public static authenticated(options?: {
    baseUrl?: string;
    token?: string;
  }): EasyPosApiClient {
    return new EasyPosApiClient(options?.baseUrl).setAuthToken(
      options?.token ?? ApiFactory.instance.authToken ?? ''
    );
  }
  public static anonymous(options?: { baseUrl?: string }): EasyPosApiClient {
    return new EasyPosApiClient(options?.baseUrl);
  }
}
